import { getMonthName, scheduleMonthParts } from "@repo/shared";
import type { SavingsPlanItem } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/use-confirm";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { FollowupSelectionDialog } from "./followup-selection-dialog";
import { useFrequencySuffix } from "./frequency-suffix";
import { T } from "./savings-plans-section.i18n";

type SavingsPlansSectionProps = {
  clientId: string;
  savingsPlans: SavingsPlanItem[];
  /** Client accounts, used to resolve product lines' soft accountId to a name. */
  accounts: Array<{ id: string; name: string }>;
};

function formatCurrency(value: number, currency?: string | null): string {
  if (!currency) {
    return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(value);
  }
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    maximumFractionDigits: 0,
  }).format(value);
}

// QL-688 fix round 2 — `startDate`/`endDate` arrive as zone-less Postgres
// strings (e.g. "2026-10-01 00:00:00"); `new Date(...)` on that string parses
// it as local time, so a browser east of UTC (including Norway) shows the
// month one early. `scheduleMonthParts` (shared, unit-tested with pinned
// timezones since this surface has no test harness) parses it as UTC first
// before reading the month/year — same approach `portfolio-list-row.tsx`
// uses via `parseScheduleDate` for this identical problem. Null/unparsable
// values fall back to the dash shown for an unset date.
function formatPlanMonth(date: string | null, language: string, notSet: string): string {
  const parts = date ? scheduleMonthParts(date) : null;
  return parts ? `${getMonthName(parts.month, language, "long")} ${parts.year}` : notSet;
}

export function SavingsPlansSection({
  clientId,
  savingsPlans,
  accounts,
}: SavingsPlansSectionProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const frequencySuffix = useFrequencySuffix();
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [followupPlan, setFollowupPlan] = useState<SavingsPlanItem | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const showFollowupError = (message: string) => {
    void confirm({
      title: t.error,
      description: message,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });
  };

  // Guard only — the tab trigger itself is hidden when the client has no
  // plans (QL-150 step 2: plans live on their own tab).
  if (savingsPlans.length === 0) return null;

  const accountNameById = new Map(accounts.map((a) => [a.id, a.name]));

  const toggleExpanded = (planId: string) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  };

  // Sum of the money-type lines — the plan's per-installment total. Units-type
  // lines have no currency value and are excluded from the sum.
  const installmentTotal = (plan: SavingsPlanItem): number =>
    plan.products.filter((p) => p.type === "amount").reduce((sum, p) => sum + p.size, 0);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">{t.savingsPlans}</h3>
        <p className="text-sm text-muted-foreground">{t.savingsPlansDescription}</p>
      </div>

      <div className="space-y-2">
        {savingsPlans.map((plan) => {
          const isExpanded = expandedPlans.has(plan.id);
          const total = installmentTotal(plan);

          return (
            <div key={plan.id} className="border rounded-lg">
              {/* Plan header row */}
              {/* biome-ignore lint/a11y/useSemanticElements: div with role="button" needed due to nested interactive elements */}
              <div
                role="button"
                tabIndex={0}
                className={cn(
                  "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50",
                  isExpanded && "border-b",
                )}
                onClick={() => toggleExpanded(plan.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleExpanded(plan.id);
                  }
                }}
              >
                <button
                  type="button"
                  className="p-1 hover:bg-muted rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(plan.id);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{plan.name}</span>
                    {/* Defensive per-status badge — the internal list is
                        active-only server-side, so pending never renders. */}
                    {plan.status === "pending" ? (
                      <span className="text-xs text-warning bg-warning/10 px-2 py-0.5 rounded">
                        {t.pending}
                      </span>
                    ) : (
                      <span className="text-xs text-success bg-success/10 px-2 py-0.5 rounded">
                        {t.active}
                      </span>
                    )}
                    {plan.vendor && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {plan.vendor}
                      </span>
                    )}
                    {plan.goal && (
                      <span
                        className="text-xs text-info bg-info/10 px-2 py-0.5 rounded"
                        title={plan.goal.name ?? undefined}
                      >
                        {plan.goal.name}
                      </span>
                    )}
                  </div>
                  {plan.externalId && (
                    <div className="text-xs text-muted-foreground font-mono">{plan.externalId}</div>
                  )}
                </div>

                {/* QL-150 step 3 — follow-up entry point: active plans only
                    (a pending placeholder has no bank-confirmed content). */}
                <div className="w-28 shrink-0 flex justify-end">
                  {plan.status === "active" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFollowupPlan(plan);
                      }}
                    >
                      {t.followup}
                    </Button>
                  )}
                </div>

                <div className="text-right shrink-0">
                  <div className="font-medium">
                    {formatCurrency(total, plan.currency)}
                    <span className="text-xs text-muted-foreground">
                      {frequencySuffix(plan.frequency)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {plan.products.length} {plan.products.length === 1 ? t.line : t.lines}
                  </div>
                </div>
              </div>

              {/* Expanded: plan details + product lines */}
              {isExpanded && (
                <div className="p-3 text-sm space-y-3">
                  <div className="grid grid-cols-[8rem_1fr] md:grid-cols-[8rem_1fr_8rem_1fr] gap-x-4 gap-y-1">
                    <div className="text-muted-foreground">{t.startDate}</div>
                    <div>{formatPlanMonth(plan.startDate, userLanguage, t.notSet)}</div>
                    <div className="text-muted-foreground">{t.endDate}</div>
                    <div>{formatPlanMonth(plan.endDate, userLanguage, t.notSet)}</div>
                  </div>

                  {plan.products.length > 0 && (
                    <div className="rounded-md overflow-hidden border">
                      {plan.products.map((p, i) => (
                        <div
                          key={p.id}
                          className={cn(
                            "flex items-center gap-3 px-4 py-2",
                            i % 2 === 0 && "bg-muted/30",
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.name || p.isin}</div>
                            <div className="text-xs text-muted-foreground font-mono">{p.isin}</div>
                          </div>
                          {/* accountId is a soft reference — render nothing when
                            it doesn't resolve to a known account. */}
                          {p.accountId && accountNameById.has(p.accountId) && (
                            <div className="text-xs text-muted-foreground shrink-0">
                              {t.account}: {accountNameById.get(p.accountId)}
                            </div>
                          )}
                          <div className="font-medium shrink-0">
                            {p.type === "amount"
                              ? formatCurrency(p.size, plan.currency)
                              : `${p.size} ${t.units}`}
                            <span className="text-xs text-muted-foreground">
                              {frequencySuffix(plan.frequency)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmationDialog />
      <FollowupSelectionDialog
        clientId={clientId}
        target={
          followupPlan
            ? {
                kind: "agreement",
                planId: followupPlan.id,
                goalFlowInstanceId: followupPlan.goal?.flowInstanceId ?? null,
              }
            : null
        }
        onClose={() => setFollowupPlan(null)}
        onError={showFollowupError}
      />
    </div>
  );
}
